import React, {
  ReactNode,
  createContext,
  useContext,
  useCallback
} from 'react';
import { Redirect } from 'react-router-dom';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useContent } from 'hooks/useContent/useContent';
import { LoadingOverlay } from 'components/loading/LoadingOverlay';
import { getLabel } from './labels';
import { ADD_ON_SKUS, STEP_PATH } from '../../constants';
import { ContentType } from './types';

const ContentContext = createContext({} as ContentType);
export const useContentContext = (): ContentType => useContext(ContentContext);

const getContentProductIds = (productId: string, addonIds?: string[]): number[] => {
  const addonProductIds = addonIds
    ?.filter((id) => ADD_ON_SKUS.includes(id))
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id)) ?? [];

  return Array.from(new Set([Number(productId) || 0, ...addonProductIds]));
};

const constructQueries = (productId: string, region: string, addonIds?: string[], promotionCode?: string) => (
  {
    region,
    partials: [
      {
        model: 'products',
        properties: ['productId', 'fullName'],
        filters: {
          productId: getContentProductIds(productId, addonIds)
        }
      },
      ...(promotionCode ? [{
        model: 'promotions',
        properties: ['terms'],
        filters: {
          promotionCode: [promotionCode]
        }
      }] : [])
    ].filter(Boolean)
  });

export const ContentProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const {
    productId,
    region,
    promoCode,
    addonIds,
    theme,
    subscriptionType
  } = useSessionContext();

  const {
    isError,
    isLoading,
    data
  } = useContent(constructQueries(productId, region, addonIds, promoCode));
  const getLabels = useCallback(
    (label: symbol) =>
      getLabel(label, subscriptionType, theme),
    [theme, subscriptionType]
  );

  if (isError) return <Redirect to={STEP_PATH.ERROR} />;
  const { products, promotions } = data || {};

  return (
    <ContentContext.Provider value={{ products, promotions, getLabels }}>
      <LoadingOverlay isLoading={isLoading}/>
      {children}
    </ContentContext.Provider>
  );
};
